import { useCallback, useEffect, useState } from 'react'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { Pencil, Trash2, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { computeSeatPositions, type SeatPosition } from '@/lib/seatingPlannerSeats'
import { TABLE_COLOR_PALETTE, type SeatingGuest, type SeatingTable } from '@/types/seatingPlanner'

type TableShapeProps = {
  table: SeatingTable
  /** Guests indexed by seat position; gaps render as empty chairs. */
  seatedGuests: Array<SeatingGuest | undefined>
  /** Total assigned to this table (used for over-capacity feedback). */
  assignedCount: number
  canvasScale: number
  detailed: boolean
  isDropTarget: boolean
  activeDropSeatIndex: number | null
  onUpdate: (patch: Partial<SeatingTable>) => void
  onDelete: () => void
  onUnassignGuest: (guestId: string) => void
}

export default function TableShape({
  table,
  seatedGuests,
  assignedCount,
  canvasScale,
  detailed,
  isDropTarget,
  activeDropSeatIndex,
  onUpdate,
  onDelete,
  onUnassignGuest,
}: TableShapeProps) {
  const { setNodeRef: setDragRef, attributes, listeners, transform, isDragging } = useDraggable({
    id: `table:${table.id}`,
    data: { type: 'table', tableId: table.id },
  })
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `table-drop:${table.id}`,
    data: { type: 'table', tableId: table.id },
  })

  const setRef = (node: HTMLElement | null) => {
    setDragRef(node)
    setDropRef(node)
  }

  const dragScale = canvasScale || 1
  const dx = (transform?.x ?? 0) / dragScale
  const dy = (transform?.y ?? 0) / dragScale
  const isRound = table.shape === 'round'
  const overCapacity = assignedCount > table.capacity
  const seats = computeSeatPositions(table)

  return (
    <div
      ref={setRef}
      className={cn(
        'group absolute touch-none select-none',
        isDragging && 'z-50',
      )}
      style={{
        left: table.x,
        top: table.y,
        width: table.width,
        height: table.height,
        transform: `translate3d(${dx}px, ${dy}px, 0) rotate(${isRound ? 0 : table.rotation}deg)`,
      }}
    >
      <div
        {...attributes}
        {...listeners}
        style={!overCapacity && table.color ? { backgroundColor: table.color } : undefined}
        className={cn(
          'relative flex h-full w-full cursor-grab flex-col items-center justify-center text-foreground shadow-md ring-2 transition-shadow',
          isRound ? 'rounded-full' : 'rounded-lg',
          overCapacity ? 'ring-red-500 bg-red-50' : 'ring-primary/40',
          !overCapacity && !table.color && 'bg-white',
          isOver || isDropTarget ? 'ring-primary ring-4' : '',
          isDragging ? 'cursor-grabbing shadow-2xl' : 'hover:ring-primary',
        )}
      >
        <TableCenter table={table} assignedCount={assignedCount} overCapacity={overCapacity} />
      </div>

      {seats.map((seat) => {
        const guest = seatedGuests[seat.index]
        return (
          <Chair
            key={seat.index}
            tableId={table.id}
            seat={seat}
            guest={guest}
            canvasScale={canvasScale}
            showName={detailed}
            isDropTarget={activeDropSeatIndex === seat.index}
            onUnassign={guest ? () => onUnassignGuest(guest.id) : undefined}
          />
        )
      })}

      <div className="absolute -top-2 -right-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        <EditTablePopover table={table} onUpdate={onUpdate} />
        <Button
          type="button"
          size="icon"
          variant="destructive"
          className="size-7 rounded-full shadow-sm"
          onClick={onDelete}
          aria-label="Delete table"
        >
          <Trash2 className="size-3.5" />
        </Button>
      </div>
    </div>
  )
}

const CHAIR_SIZE_PX = 22
const NAME_OFFSET_PX = 24

function Chair({
  tableId,
  seat,
  guest,
  canvasScale,
  showName,
  isDropTarget,
  onUnassign,
}: {
  tableId: string
  seat: SeatPosition
  guest?: SeatingGuest
  canvasScale: number
  showName: boolean
  isDropTarget: boolean
  onUnassign?: () => void
}) {
  const occupied = Boolean(guest)
  const {
    setNodeRef: setDragRef,
    attributes,
    listeners,
    transform,
    isDragging,
  } = useDraggable({
    id: `seat:${tableId}:${seat.index}`,
    data: { type: 'guest', guestId: guest?.id, fromTableId: tableId },
    disabled: !occupied,
  })
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `seat-drop:${tableId}:${seat.index}`,
    data: { type: 'seat', tableId, seatIndex: seat.index },
  })
  const setRefs = useCallback(
    (node: HTMLElement | null) => {
      setDragRef(node)
      setDropRef(node)
    },
    [setDragRef, setDropRef],
  )

  const dragScale = canvasScale || 1
  const dx = (transform?.x ?? 0) / dragScale
  const dy = (transform?.y ?? 0) / dragScale
  // Rotate so the backrest faces outward along (nx, ny). With the chair drawn
  // back-on-top / cushion-on-bottom, rotating to atan2(ny, nx) + 90° aligns
  // the local "up" direction with the outward normal.
  const rotationDeg = (Math.atan2(seat.ny, seat.nx) * 180) / Math.PI + 90
  const highlight = isOver || isDropTarget

  return (
    <>
      {showName && guest && !isDragging && (
        <div
          className="pointer-events-none absolute z-10 max-w-28 -translate-x-1/2 -translate-y-1/2 rounded bg-white/95 px-1.5 py-0.5 text-[10px] leading-tight font-medium text-foreground shadow-xs ring-1 ring-black/5"
          style={{
            left: seat.x + seat.nx * NAME_OFFSET_PX,
            top: seat.y + seat.ny * NAME_OFFSET_PX,
          }}
        >
          <span className="block truncate text-center">{guest.name}</span>
        </div>
      )}
      <button
        ref={setRefs}
        type="button"
        {...(occupied ? attributes : {})}
        {...(occupied ? listeners : {})}
        onClick={(e) => {
          e.stopPropagation()
          if (occupied) onUnassign?.()
        }}
        title={
          guest
            ? `Drag ${guest.name} to another seat or click to unseat`
            : `Empty seat ${seat.index + 1} — drop a guest here`
        }
        aria-label={guest ? `Seat for ${guest.name}` : `Empty seat ${seat.index + 1}`}
        className={cn(
          'absolute block p-0 transition-transform',
          occupied ? 'cursor-grab active:cursor-grabbing' : 'cursor-default',
          highlight && 'scale-110',
          isDragging ? 'z-50 opacity-50' : 'z-0',
        )}
        style={{
          left: seat.x,
          top: seat.y,
          width: CHAIR_SIZE_PX,
          height: CHAIR_SIZE_PX,
          transform: `translate(-50%, -50%) translate3d(${dx}px, ${dy}px, 0) rotate(${rotationDeg}deg)`,
        }}
      >
        <span
          className={cn(
            'absolute top-0 right-1 left-1 h-[5px] rounded-t-sm transition-colors',
            occupied ? 'bg-primary/70' : 'bg-muted-foreground/30',
            highlight && 'bg-primary',
          )}
        />
        <span
          className={cn(
            'absolute right-0 bottom-0 left-0 h-[15px] rounded-md ring-1 transition-colors',
            occupied
              ? 'bg-primary ring-primary/60 shadow-sm'
              : 'bg-white ring-muted-foreground/30',
            highlight && 'ring-2 ring-primary',
            isDragging && 'shadow-md',
          )}
        />
      </button>
    </>
  )
}

function TableCenter({
  table,
  assignedCount,
  overCapacity,
}: {
  table: SeatingTable
  assignedCount: number
  overCapacity: boolean
}) {
  return (
    <div className="pointer-events-none flex flex-col items-center justify-center gap-0.5 px-2 text-center">
      <div className="text-xl leading-none font-semibold tracking-tight">{table.number || '?'}</div>
      {table.label && (
        <div className="line-clamp-1 max-w-full text-[11px] font-medium text-muted-foreground">{table.label}</div>
      )}
      <div
        className={cn(
          'mt-0.5 inline-flex items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium',
          overCapacity && 'bg-red-100 text-red-800',
        )}
      >
        <Users className="size-2.5" />
        {assignedCount}/{table.capacity}
      </div>
    </div>
  )
}

function EditTablePopover({
  table,
  onUpdate,
}: {
  table: SeatingTable
  onUpdate: (patch: Partial<SeatingTable>) => void
}) {
  const [capacityDraft, setCapacityDraft] = useState(() => String(table.capacity))

  useEffect(() => {
    setCapacityDraft(String(table.capacity))
  }, [table.id, table.capacity])

  const commitCapacityDraft = () => {
    const nextValue = capacityDraft.trim()
    const nextCapacity = parseInt(nextValue, 10)

    if (Number.isFinite(nextCapacity) && nextCapacity > 0) {
      if (nextCapacity !== table.capacity) onUpdate({ capacity: nextCapacity })
      return
    }

    setCapacityDraft(String(table.capacity))
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant="secondary"
          className="size-7 rounded-full shadow-sm"
          onPointerDown={(e) => e.stopPropagation()}
          aria-label="Edit table"
        >
          <Pencil className="size-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="start" onPointerDown={(e) => e.stopPropagation()}>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor={`table-${table.id}-number`}>Table number</Label>
            <Input
              id={`table-${table.id}-number`}
              value={table.number}
              onChange={(e) => onUpdate({ number: e.target.value })}
              placeholder="e.g. 12 or Head"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`table-${table.id}-label`}>Table name (optional)</Label>
            <Input
              id={`table-${table.id}-label`}
              value={table.label}
              onChange={(e) => onUpdate({ label: e.target.value })}
              placeholder="e.g. Head Table, VIPs, Sponsors"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor={`table-${table.id}-shape`}>Shape</Label>
              <Select
                value={table.shape}
                onValueChange={(v) => onUpdate({ shape: v as 'round' | 'rectangle' })}
              >
                <SelectTrigger id={`table-${table.id}-shape`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="round">Round</SelectItem>
                  <SelectItem value="rectangle">Rectangle</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor={`table-${table.id}-capacity`}>Seats</Label>
              <Input
                id={`table-${table.id}-capacity`}
                type="number"
                min={1}
                max={40}
                value={capacityDraft}
                onChange={(e) => {
                  setCapacityDraft(e.target.value)
                }}
                onBlur={commitCapacityDraft}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    commitCapacityDraft()
                    e.currentTarget.blur()
                  }
                  if (e.key === 'Escape') {
                    setCapacityDraft(String(table.capacity))
                    e.currentTarget.blur()
                  }
                }}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor={`table-${table.id}-w`}>Width</Label>
              <Input
                id={`table-${table.id}-w`}
                type="number"
                min={60}
                max={500}
                value={table.width}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10)
                  if (Number.isFinite(n) && n > 0) {
                    const patch: Partial<SeatingTable> = { width: n }
                    if (table.shape === 'round') patch.height = n
                    onUpdate(patch)
                  }
                }}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`table-${table.id}-h`}>Height</Label>
              <Input
                id={`table-${table.id}-h`}
                type="number"
                min={60}
                max={500}
                value={table.height}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10)
                  if (Number.isFinite(n) && n > 0) {
                    const patch: Partial<SeatingTable> = { height: n }
                    if (table.shape === 'round') patch.width = n
                    onUpdate(patch)
                  }
                }}
                disabled={table.shape === 'round'}
              />
            </div>
          </div>
          {table.shape === 'rectangle' && (
            <div className="space-y-1">
              <Label htmlFor={`table-${table.id}-rot`}>Rotation</Label>
              <Input
                id={`table-${table.id}-rot`}
                type="number"
                min={-180}
                max={180}
                value={table.rotation}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10)
                  if (Number.isFinite(n)) onUpdate({ rotation: n })
                }}
              />
            </div>
          )}
          <div className="space-y-1">
            <Label>Color</Label>
            <div className="flex flex-wrap gap-1.5">
              {TABLE_COLOR_PALETTE.map((c) => {
                const selected = (table.color ?? null) === c.value
                return (
                  <button
                    key={c.label}
                    type="button"
                    onClick={() =>
                      onUpdate({ color: c.value === null ? undefined : c.value })
                    }
                    title={c.label}
                    aria-label={`Set color to ${c.label}`}
                    className={cn(
                      'size-7 rounded-full border border-black/10 transition-shadow',
                      selected ? 'ring-2 ring-primary ring-offset-2' : 'hover:ring-2 hover:ring-primary/40',
                    )}
                    style={{ backgroundColor: c.swatch }}
                  />
                )
              })}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
